import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import getDisabledChoices from 'lib/getCarDisabledChoices'
import T from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { mapLocation } from 'lib/carDetailsLib'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import HeartDownloadIcon from 'assets/images/icons/heart-download'
import { getCarContextKey, getSessionIdFromCar } from 'lib/carContextKey'
import { isRepairSession } from 'lib/sessionLifecycle'

import CarCard from 'components/atoms/CarCard'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarsList from 'components/atoms/CarsList'
import CommentLink from 'components/atoms/CommentLink'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import PagePartnersSelect from 'components/atoms/PagePartnersSelect'
import PageTitle from 'components/atoms/PageTitle'
import SearchField from 'components/atoms/SearchField'

import useCarsFilters from 'hooks/useCarsFilters'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useExport from 'hooks/useExport'
import useInputOutput from 'hooks/useInputOutput'

import CarCardChoices from './CarCardChoices'
import CarBlindPrice from './CarBlindPrice'
import CarReprice from './CarReprice'
import CarsToBuyDetails from './CarsToBuyDetails'
import CarsToBuyPageTitleRightColumn from './CarsToBuyPageTitleRightColumn'
import CarsToBuySessionExpired from './CarsToBuySessionExpired'
import ChoicesRecap from './ChoicesRecap'
import MessageDialog from './MessageDialog'
import {
  getBlindOffersRemainingByType,
  getBlindOffersUsedByType,
  normalizeBlindOffersRemaining,
} from './blindOffersLib'
import { getCarsToBuyUrl } from './carsToBuyLib'

const getBlindOffersCount = ({ count, positiveCount, negativeCount }) => {
  const parseCount = (value) => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  const parsedCount = parseCount(count)
  if (parsedCount !== null) {
    return parsedCount
  }

  const parsedPositiveCount = parseCount(positiveCount)
  const parsedNegativeCount = parseCount(negativeCount)
  const hasPositiveCount = parsedPositiveCount !== null
  const hasNegativeCount = parsedNegativeCount !== null

  if (!hasPositiveCount && !hasNegativeCount) {
    return 0
  }

  return (
    (hasPositiveCount ? parsedPositiveCount : 0) +
    (hasNegativeCount ? parsedNegativeCount : 0)
  )
}

const hasBlindOffer = (car) =>
  getBlindOffersCount({
    count: car?.blindOfferCount,
    positiveCount: car?.blindPositiveOfferCount,
    negativeCount: car?.blindNegativeOfferCount,
  }) > 0

const hasValue = (value) =>
  value !== null && value !== undefined && value !== ''

const getParsedCount = (value) => {
  if (!hasValue(value)) {
    return null
  }
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const sumOptionalCounts = (left, right) => {
  const parsedLeft = getParsedCount(left)
  const parsedRight = getParsedCount(right)

  if (parsedLeft === null && parsedRight === null) {
    return null
  }

  return (parsedLeft || 0) + (parsedRight || 0)
}

const getClampedBlindSplitCount = (value, maxCount) => {
  const parsedValue = getParsedCount(value)
  if (parsedValue === null) {
    return null
  }
  if (maxCount === null) {
    return parsedValue
  }
  return Math.min(parsedValue, maxCount)
}

const getResolvedBlindCounts = ({ count, positiveCount, negativeCount }) => {
  const parsedCount = getParsedCount(count)
  const parsedPositiveCount = getClampedBlindSplitCount(
    positiveCount,
    parsedCount
  )
  const parsedNegativeCount = getClampedBlindSplitCount(
    negativeCount,
    parsedCount
  )

  return {
    count: parsedCount,
    positiveCount: parsedPositiveCount,
    negativeCount: parsedNegativeCount,
  }
}

const getResolvedHasBlindPositiveOffer = ({
  hasBlindPositiveOffer,
  positiveCount,
  negativeCount,
  count,
}) => {
  if (typeof hasBlindPositiveOffer === 'boolean') {
    return hasBlindPositiveOffer
  }

  const parsedPositiveCount = getParsedCount(positiveCount)
  const parsedNegativeCount = getParsedCount(negativeCount)
  if (parsedPositiveCount !== null || parsedNegativeCount !== null) {
    return (parsedPositiveCount || 0) > 0
  }

  const parsedCount = getParsedCount(count)
  if (parsedCount !== null) {
    return parsedCount > 0
  }

  return null
}

const getSelectedChoicesForCar = ({ session, selectedCarsToBuy, car }) => {
  if (!session.showOtherPartners) {
    return undefined
  }

  const targetKey = getCarContextKey(car, session?.sessionId)
  const selectedChoices = selectedCarsToBuy?.find(
    (selectedCar) =>
      getCarContextKey(selectedCar, session?.sessionId) === targetKey
  )

  if (!selectedChoices) {
    return selectedChoices
  }

  const resolvedBlindCounts = getResolvedBlindCounts({
    count:
      selectedChoices.count ??
      selectedChoices.blindCount ??
      selectedChoices.blindOffers,
    positiveCount: selectedChoices.blindPositiveCount,
    negativeCount: selectedChoices.blindNegativeCount,
  })

  return {
    ...selectedChoices,
    blindPositiveCount:
      resolvedBlindCounts.positiveCount ?? selectedChoices.blindPositiveCount,
    blindNegativeCount:
      resolvedBlindCounts.negativeCount ?? selectedChoices.blindNegativeCount,
    hasBlindPositiveOffer: getResolvedHasBlindPositiveOffer({
      hasBlindPositiveOffer: selectedChoices.hasBlindPositiveOffer,
      positiveCount:
        resolvedBlindCounts.positiveCount ?? selectedChoices.blindPositiveCount,
      negativeCount:
        resolvedBlindCounts.negativeCount ?? selectedChoices.blindNegativeCount,
      count:
        resolvedBlindCounts.count ??
        selectedChoices.blindCount ??
        selectedChoices.blindOffers,
    }),
  }
}

const getFavoriteToggleAction = ({ showFavorites, onToggleFavorite, car }) =>
  showFavorites ? () => onToggleFavorite(car.plate, car.favorite) : undefined

const getDetailsTargetFromCar = (car, fallbackSessionId) => {
  if (!car?.plate) {
    return null
  }

  const resolvedSessionId =
    getSessionIdFromCar(car) ?? fallbackSessionId ?? null
  return {
    plate: car.plate,
    sessionId: resolvedSessionId,
    contextKey: getCarContextKey(car, fallbackSessionId),
  }
}

const normalizeDetailsTarget = (target, cars, fallbackSessionId) => {
  if (!target) {
    return null
  }

  if (typeof target === 'string') {
    const matchedCar = cars.find((car) => car.plate === target)
    return (
      getDetailsTargetFromCar(matchedCar, fallbackSessionId) || {
        plate: target,
        sessionId: fallbackSessionId || null,
        contextKey: getCarContextKey(
          { plate: target, sessionId: fallbackSessionId || null },
          fallbackSessionId || null
        ),
      }
    )
  }

  if (!target?.plate) {
    return null
  }

  const resolvedSessionId =
    target.sessionId ?? fallbackSessionId ?? getSessionIdFromCar(target) ?? null
  return {
    plate: target.plate,
    sessionId: resolvedSessionId,
    contextKey:
      target.contextKey ||
      getCarContextKey(
        { plate: target.plate, sessionId: resolvedSessionId },
        fallbackSessionId || null
      ),
  }
}

const findCarByDetailsTarget = (cars, target, fallbackSessionId) => {
  if (!target) {
    return null
  }

  if (target.contextKey) {
    const byContext = cars.find(
      (car) => getCarContextKey(car, fallbackSessionId) === target.contextKey
    )
    if (byContext) {
      return byContext
    }
  }

  return cars.find((car) => car.plate === target.plate) || null
}

const shouldRenderBlindPriceForCar = ({ admin, car }) =>
  !admin || hasValue(car.blindPrice) || hasBlindOffer(car)

const mapCars = ({
  admin,
  blindOffersRemaining,
  disabledChoices,
  filteredCars,
  handleConfirmReprice,
  handleConfirmBlindPrice,
  getSelection,
  onSelectionChange,
  partnerId,
  readOnly,
  repairSession,
  blindReadOnly,
  selectedCarsToBuy,
  showBlindPrice,
  showBlindOption,
  showBlindPositiveControls,
  showNonBlindReprice,
  showPriceRange,
  session,
  setShowDetails,
  onCommentClick,
  contentIsReadOnly,
  onToggleFavorite,
  showFavorites,
}) =>
  filteredCars.map((car, i) => {
    const selectedChoicesForCar = getSelectedChoicesForCar({
      session,
      selectedCarsToBuy,
      car,
    })
    const selection = getSelection(car)
    const showPriceSection = showNonBlindReprice || showBlindPrice
    const showBlindPriceForCar =
      showBlindPrice &&
      selection === 'blind' &&
      shouldRenderBlindPriceForCar({ admin, car })

    return (
      <CarCard
        data-testid={`car-${i}`}
        key={car.originalId}
        onClick={() => setShowDetails(car)}
        height={showPriceSection ? 225 : 209}
        showPriceRange={showPriceRange}
        car={car}
        price={car.price}
        maxPrice={car.maxPrice}
        onToggleFavorite={getFavoriteToggleAction({
          showFavorites,
          onToggleFavorite,
          car,
        })}
        right={
          <CarCardChoices
            session={session}
            partnerId={partnerId}
            car={car}
            disabledChoices={disabledChoices}
            blindOffersRemaining={blindOffersRemaining}
            selectedChoices={selectedChoicesForCar}
            readOnly={admin || readOnly || contentIsReadOnly}
            selection={selection}
            onSelectionChange={onSelectionChange}
            showBlindOption={showBlindOption}
            showBlindPositiveSignal={showBlindPositiveControls}
          />
        }
        bottom={
          <Stack direction="row">
            <Stack
              mt={2}
              mr={2}
              justifyContent="center"
              sx={{ flex: '1 0 auto' }}
            >
              <CommentLink
                handleClick={() => onCommentClick(car)}
                commentsNumber={car?.messages?.length}
                contentIsReadOnly={contentIsReadOnly}
                advisory={car?.publicAdvice}
              />
            </Stack>
            {showPriceSection && (
              <Stack direction="column" spacing={1} sx={{ width: 1 }}>
                {showNonBlindReprice && (
                  <CarReprice
                    car={car}
                    handleConfirmReprice={handleConfirmReprice}
                    handleShowDetails={() => setShowDetails(car)}
                    readOnly={readOnly}
                    sx={{ width: 1 }}
                  />
                )}
                {showBlindPriceForCar && (
                  <CarBlindPrice
                    car={car}
                    handleConfirmBlindPrice={handleConfirmBlindPrice}
                    handleShowDetails={() => setShowDetails(car)}
                    readOnly={admin || blindReadOnly}
                    showConfirmButton={!admin}
                    enabled={selection === 'blind'}
                    blindOffersRemaining={blindOffersRemaining}
                    allowBlindPositiveOffers={showBlindPositiveControls}
                    showRepairHint={repairSession}
                    sx={{ width: 1 }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        }
      />
    )
  })

function getRowsForDownload(titlesRow, cars, t) {
  return [
    titlesRow,
    ...cars.map((car) => [
      `${car.title}`,
      car.km,
      mapLocation(car.location),
      t(`carDetails.mapping.${car.transmission}`),
      car.reconditioningEstCost || '-',
      car.year,
      car.fuelType,
      car.plate,
      Math.round(car.optionAmount) || '-',
      car.firstChoice
        ? '1'
        : car.blindPrice !== null && car.blindPrice !== undefined
        ? t('carsToBuy.choice.blind')
        : car.secondChoice
        ? '2'
        : '-',
      car.favorite
        ? t('common.filters.boolean.true')
        : t('common.filters.boolean.false'),
    ]),
  ]
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function CarsToBuy({
  session,
  previousSession,
  admin,
  partnerId,
  partners,
  updateChoice = () => {},
  setReprice = () => {},
  addMessage,
  toggleFavorite = () => {},
  cars,
  selectedCarsToBuy,
  countDown,
  readOnly,
  contentIsReadOnly = false,
  showFavorites = false,
  isBlindPublication = false,
  showHeader = true,
  showPartnerSelect = true,
  title,
  stateKey,
  showBlindPrice = false,
  blindOffersRemaining,
  blindReadOnly = false,
  setBlindPrice = () => {},
  blindSessionId,
}) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showDetails, setShowDetails] = useState(null)
  const [filteredCars, setFilteredCars] = useState([])
  const [selectedMessageTarget, setSelectedMessageTarget] = useState(null)
  const [blindSelectionByPlate, setBlindSelectionByPlate] = useState({})
  const [selectionByPlate, setSelectionByPlate] = useState({})

  const { saveXls } = useInputOutput()
  const { saveExport, isFetching: isFetchingExport } = useExport()

  const caps = useMemo(
    () =>
      (isBlindPublication ? session.partnerBlindCaps : session.partnerCaps) ||
      {},
    [isBlindPublication, session]
  )

  const resolvedBlindOffersRemaining = useMemo(() => {
    if (hasValue(blindOffersRemaining)) {
      return normalizeBlindOffersRemaining(blindOffersRemaining)
    }

    if (!isBlindPublication) {
      return normalizeBlindOffersRemaining(blindOffersRemaining)
    }

    return getBlindOffersRemainingByType({
      caps,
      cars,
    })
  }, [blindOffersRemaining, caps, cars, isBlindPublication])

  const blindOffersUsedByType = useMemo(
    () => getBlindOffersUsedByType(cars),
    [cars]
  )

  const blindOffersTotalByType = useMemo(
    () => ({
      positive: sumOptionalCounts(
        resolvedBlindOffersRemaining?.positive,
        blindOffersUsedByType?.positive
      ),
      negative: sumOptionalCounts(
        resolvedBlindOffersRemaining?.negative,
        blindOffersUsedByType?.negative
      ),
    }),
    [resolvedBlindOffersRemaining, blindOffersUsedByType]
  )

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setCaps: setFilteringCaps,
    setOnlyFavorites,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(addMessage)

  useEffect(() => {
    if (!cars.length || filters?.initialized) {
      return
    }

    initFilters(cars)
  }, [cars, filters?.initialized, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const disabledChoices = useMemo(
    () => getDisabledChoices(cars, caps),
    [caps, cars]
  )

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const handleSetFilteringCaps = (value) => {
    setFilteringCaps(value)
    if (filters.onlyFavorites) {
      setOnlyFavorites(false)
    }
  }

  const handleToggleFavoritesFilter = () => {
    const newValue = !filters.onlyFavorites
    setOnlyFavorites(newValue)
    // If turning on favorites filter, reset the caps filter
    if (newValue && filters.caps) {
      setFilteringCaps('')
    }
  }

  const handleConfirmReprice = useCallback(
    (plate, reprice) => {
      setReprice(session.sessionId, partnerId, plate, reprice)
    },
    [partnerId, session, setReprice]
  )

  const showNonBlindReprice = !!session.canReprice && !isBlindPublication
  const repairSession = isRepairSession(session)
  const blindPriceSessionId =
    blindSessionId || (isBlindPublication ? session.sessionId : null)
  const resolvedShowBlindOption = isBlindPublication || showBlindPrice
  const resolvedShowBlindPrice =
    !admin && resolvedShowBlindOption && !!blindPriceSessionId
  const showBlindOption = resolvedShowBlindOption
  const showBlindPositiveControls = !repairSession

  const getSelection = useCallback(
    (car) => {
      const contextKey = getCarContextKey(car, session?.sessionId)
      const hasLocalBlindSelection =
        contextKey && blindSelectionByPlate[contextKey]
      const hasBlindPrice =
        car.blindPrice !== null && car.blindPrice !== undefined

      if (contextKey && selectionByPlate[contextKey]) {
        return selectionByPlate[contextKey]
      }
      if (car.firstChoice) {
        return 'selected'
      }
      if (hasBlindPrice) {
        return 'blind'
      }
      return hasLocalBlindSelection ? 'blind' : 'none'
    },
    [blindSelectionByPlate, selectionByPlate, session?.sessionId]
  )

  const clearBlindSelection = useCallback(
    (plate) => {
      const contextKey = getCarContextKey(
        { plate, sessionId: session?.sessionId },
        session?.sessionId
      )
      if (!contextKey) {
        return
      }

      setBlindSelectionByPlate((current) => {
        if (!current[contextKey]) {
          return current
        }
        const next = { ...current }
        delete next[contextKey]
        return next
      })
    },
    [session?.sessionId]
  )

  const handleSelectionChange = useCallback(
    async (car, nextSelection) => {
      if (readOnly) {
        return false
      }

      if (nextSelection === 'blind') {
        if (car.firstChoice) {
          const success = await updateChoice(
            session.sessionId,
            partnerId,
            car.plate,
            'none'
          )
          if (!success) {
            return false
          }
        }
        const contextKey = getCarContextKey(car, session?.sessionId)
        if (!contextKey) {
          return false
        }
        setBlindSelectionByPlate((current) => ({
          ...current,
          [contextKey]: true,
        }))
        setSelectionByPlate((current) => ({
          ...current,
          [contextKey]: 'blind',
        }))
        return true
      }

      const choice = nextSelection === 'selected' ? 'first' : 'none'
      const success = await updateChoice(
        session.sessionId,
        partnerId,
        car.plate,
        choice
      )
      if (!success) {
        return false
      }

      clearBlindSelection(car.plate)
      const contextKey = getCarContextKey(car, session?.sessionId)
      if (contextKey) {
        setSelectionByPlate((current) => ({
          ...current,
          [contextKey]: nextSelection,
        }))
      }
      if (
        resolvedShowBlindPrice &&
        car.blindPrice !== null &&
        car.blindPrice !== undefined
      ) {
        await setBlindPrice(blindPriceSessionId, partnerId, car.plate, null)
      }

      return true
    },
    [
      readOnly,
      updateChoice,
      session?.sessionId,
      partnerId,
      clearBlindSelection,
      resolvedShowBlindPrice,
      setBlindPrice,
      blindPriceSessionId,
    ]
  )

  const handleConfirmBlindPrice = useCallback(
    async (plate, reprice) => {
      if (!blindPriceSessionId) {
        return
      }
      await setBlindPrice(blindPriceSessionId, partnerId, plate, reprice)
      if (reprice === null || reprice === undefined || reprice === '') {
        clearBlindSelection(plate)
        const contextKey = getCarContextKey(
          { plate, sessionId: session?.sessionId },
          session?.sessionId
        )
        if (contextKey) {
          setSelectionByPlate((current) => ({
            ...current,
            [contextKey]: 'none',
          }))
        }
      }
    },
    [
      blindPriceSessionId,
      partnerId,
      setBlindPrice,
      clearBlindSelection,
      session?.sessionId,
    ]
  )

  const onCommentClick = useCallback(
    (car) => {
      setSelectedMessageTarget(getDetailsTargetFromCar(car, session?.sessionId))
      handleOpenConfirmationDialog()
    },
    [handleOpenConfirmationDialog, session?.sessionId]
  )

  const onToggleFavorite = useCallback(
    (plate, currentFavoriteState) => {
      toggleFavorite(session.sessionId, partnerId, plate, currentFavoriteState)
    },
    [toggleFavorite, session.sessionId, partnerId]
  )

  const handleResetPlateOnCloseConfirmationDialog = useCallback(() => {
    setSelectedMessageTarget(null)
    handleCloseConfirmationDialog()
  }, [handleCloseConfirmationDialog])

  const handleSetShowDetails = useCallback(
    (nextTarget) => {
      if (!nextTarget) {
        setShowDetails(null)
        return
      }

      const normalizedTarget = normalizeDetailsTarget(
        nextTarget,
        filteredCars?.length ? filteredCars : cars,
        session?.sessionId
      )
      setShowDetails(normalizedTarget)
    },
    [cars, filteredCars, session?.sessionId]
  )

  const selectedMessageCar = useMemo(
    () =>
      findCarByDetailsTarget(cars, selectedMessageTarget, session?.sessionId),
    [cars, selectedMessageTarget, session?.sessionId]
  )

  const carsContent = useMemo(
    () =>
      mapCars({
        admin,
        blindOffersRemaining: resolvedBlindOffersRemaining,
        disabledChoices,
        filteredCars,
        handleConfirmReprice,
        handleConfirmBlindPrice,
        getSelection,
        onSelectionChange: handleSelectionChange,
        partnerId,
        readOnly,
        repairSession,
        blindReadOnly,
        selectedCarsToBuy,
        showBlindPrice: resolvedShowBlindPrice,
        showBlindOption,
        showBlindPositiveControls,
        showNonBlindReprice,
        session,
        setShowDetails: handleSetShowDetails,
        onCommentClick,
        contentIsReadOnly,
        onToggleFavorite,
        showFavorites,
        showPriceRange: !isBlindPublication && !resolvedShowBlindPrice,
      }),
    [
      admin,
      disabledChoices,
      filteredCars,
      handleConfirmReprice,
      handleConfirmBlindPrice,
      getSelection,
      handleSelectionChange,
      resolvedBlindOffersRemaining,
      partnerId,
      readOnly,
      repairSession,
      blindReadOnly,
      selectedCarsToBuy,
      session,
      handleSetShowDetails,
      onCommentClick,
      contentIsReadOnly,
      onToggleFavorite,
      showFavorites,
      resolvedShowBlindPrice,
      showBlindOption,
      showBlindPositiveControls,
      showNonBlindReprice,
      isBlindPublication,
    ]
  )

  const carFilters = useMemo(
    () => (
      <CarFiltersColumn
        filters={filters}
        filtersData={filtersData}
        filteredCars={
          filteredCars?.length !== cars.length ? filteredCars : undefined
        }
        setKmRange={setKmRange}
        setDamageRange={setDamageRange}
        setYearRange={setYearRange}
        setBrands={setBrands}
        setModels={setModels}
        setLocations={setLocations}
        setFuels={setFuels}
        setCategory={setCategory}
        resetFilters={resetFilters}
        readOnly={readOnly}
      />
    ),
    [
      filters,
      filtersData,
      filteredCars,
      cars.length,
      setKmRange,
      setDamageRange,
      setYearRange,
      setBrands,
      setModels,
      setLocations,
      setFuels,
      setCategory,
      resetFilters,
      readOnly,
    ]
  )

  const handlePartnerChange = (event) => {
    navigate(getCarsToBuyUrl(event.target.value, null, isBlindPublication))
  }

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.choice'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, cars, t)

    saveXls(`cars-to-buy`, rows)
  }

  const handleDownloadFavorites = async () => {
    const favoriteCars = cars.filter((car) => car.favorite)

    if (favoriteCars.length === 0) {
      return
    }

    const titlesRow = [
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.choice'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, favoriteCars, t)

    saveXls(`cars-to-buy-favorites`, rows)
  }

  const handleAdditionalFileDownload = async () => {
    await saveExport({
      name: `dati_veicoli_${session.sessionId}`,
      params: { partnerId, sessionId: session.sessionId },
      path: 'carsToBuy',
    })
  }

  const resolvedStateKey =
    stateKey || (isBlindPublication ? 'blindPublication' : 'publication')

  return showDetails?.plate ? (
    <CarsToBuyDetails
      partnerId={partnerId}
      plate={showDetails.plate}
      detailsContextKey={showDetails.contextKey}
      detailsSessionId={showDetails.sessionId}
      cars={filteredCars}
      allCars={cars}
      session={session}
      isBlindPublication={isBlindPublication}
      setReprice={setReprice}
      setBlindPrice={setBlindPrice}
      blindSessionId={blindSessionId}
      showBlindPrice={resolvedShowBlindPrice}
      blindReadOnly={blindReadOnly}
      blindOffersRemaining={resolvedBlindOffersRemaining}
      toggleFavorite={showFavorites ? toggleFavorite : undefined}
      selectedCarsToBuy={selectedCarsToBuy}
      setShowDetails={handleSetShowDetails}
      updateChoice={updateChoice}
      blindSelectionByPlate={blindSelectionByPlate}
      setBlindSelectionByPlate={setBlindSelectionByPlate}
      selectionByPlate={selectionByPlate}
      setSelectionByPlate={setSelectionByPlate}
    />
  ) : (
    <>
      {showHeader && (
        <PageTitle
          label={title || t('carsToBuy.title')}
          rightColumn={
            <>
              <CarsToBuyPageTitleRightColumn
                countDown={countDown}
                stateKey={resolvedStateKey}
              />
              {showPartnerSelect && partners.length > 0 && (
                <>
                  <Box pr={2} />
                  <PagePartnersSelect
                    partners={partners}
                    value={partnerId}
                    handleChange={handlePartnerChange}
                    readOnly={readOnly}
                  />
                </>
              )}
            </>
          }
        />
      )}
      {!session?.sessionId || (countDown.initialized && countDown.expired) ? (
        <CarsToBuySessionExpired
          session={session}
          previousSession={previousSession}
        />
      ) : (
        <>
          <ConfirmBar
            label={t('carsToBuy.confirmButton')}
            left={
              <SearchField
                placeholder={t('common.form.searchBy')}
                value={filters.query}
                onChange={handleChangeSearch}
                data-testid="text-search"
              />
            }
            leftProps={{ sx: { width: 0.5 } }}
            right={
              <>
                <DownloadBar handleDownload={handleDownload} />
                {showFavorites && (
                  <Tooltip title={t('carsToBuy.downloadFavorites')}>
                    <IconButton
                      data-testid="download-favorites"
                      aria-label={t('carsToBuy.downloadFavorites')}
                      onClick={handleDownloadFavorites}
                      disabled={!cars.some((car) => car.favorite)}
                      sx={{ ml: 0.5 }}
                    >
                      <HeartDownloadIcon
                        style={{ fontSize: 20, color: 'black' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Collapse orientation="horizontal" in={isFetchingExport}>
                  <div style={{ marginLeft: 4, marginRight: 4 }}>
                    <CircularProgress size={20} />
                  </div>
                </Collapse>
                <IconButton
                  data-testid="download-additional-file"
                  aria-label={t('carsToBuy.downloadAdditionalFile')}
                  onClick={handleAdditionalFileDownload}
                  disabled={isFetchingExport}
                >
                  <FolderOutlinedIcon style={{ color: 'black' }} />
                </IconButton>
              </>
            }
            top={
              <>
                {repairSession && showBlindOption && (
                  <Typography
                    variant="body2"
                    color="primaryText"
                    sx={{ pb: 1 }}
                    data-testid="cars-to-buy-repair-hint"
                  >
                    {t('carsToBuy.repair.publicationHint')}
                  </Typography>
                )}
                <ChoicesRecap
                  cars={cars}
                  caps={caps}
                  blindOffersUsedByType={blindOffersUsedByType}
                  blindOffersTotalByType={blindOffersTotalByType}
                  showBlindOffers={showBlindOption}
                  showBlindPositiveOffers={showBlindPositiveControls}
                  filteringCap={filters.caps}
                  setFilteringCaps={handleSetFilteringCaps}
                  onlyFavorites={
                    showFavorites ? filters.onlyFavorites : undefined
                  }
                  onToggleFavorites={
                    showFavorites ? handleToggleFavoritesFilter : undefined
                  }
                />
              </>
            }
            topProps={{
              sx: {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              },
            }}
            hideConfirm
            sticky
          />
          <CarsList left={carFilters} right={carsContent} />

          <MessageDialog
            messages={selectedMessageCar?.messages}
            openConfirmationDialog={openConfirmationDialog}
            handleCloseConfirmationDialog={
              handleResetPlateOnCloseConfirmationDialog
            }
            handleConfirmConfirmationDialog={(message) =>
              handleConfirmConfirmationDialog(
                selectedMessageTarget?.sessionId || session?.sessionId,
                partnerId,
                selectedMessageTarget?.plate,
                message
              )
            }
            readOnly={admin}
          />
        </>
      )}
    </>
  )
}

CarsToBuy.propTypes = {
  session: T.object.isRequired,
  previousSession: T.object,
  admin: T.bool,
  partnerId: T.string,
  partners: T.array,
  updateChoice: T.func,
  setReprice: T.func,
  addMessage: T.func,
  toggleFavorite: T.func,
  cars: T.array,
  selectedCarsToBuy: T.array,
  countDown: T.object,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
  showFavorites: T.bool,
  isBlindPublication: T.bool,
  showHeader: T.bool,
  showPartnerSelect: T.bool,
  title: T.string,
  stateKey: T.string,
  showBlindPrice: T.bool,
  blindOffersRemaining: T.oneOfType([
    T.number,
    T.shape({
      positive: T.number,
      negative: T.number,
    }),
  ]),
  blindReadOnly: T.bool,
  setBlindPrice: T.func,
  blindSessionId: T.string,
}

export default CarsToBuy
